import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Languages } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { API_URL } from '../../config';

export default function TranslationLocalizationSettings({ orgId, subscription, onUpdated }) {
  const addon = subscription?.translation_addon;
  const enabled = Boolean(addon?.enabled && (addon?.purchased_languages || 0) > 0);
  const purchased = Number(addon?.purchased_languages) || 0;
  const options = addon?.available_language_options || [];

  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const optionMap = useMemo(
    () => Object.fromEntries(options.map((opt) => [opt.code, opt.name])),
    [options]
  );

  useEffect(() => {
    setSelected(Array.isArray(addon?.selected_languages) ? addon.selected_languages : []);
  }, [addon?.selected_languages, orgId]);

  const toggleLanguage = (code) => {
    setSelected((prev) => {
      if (prev.includes(code)) {
        return prev.filter((c) => c !== code);
      }
      if (prev.length >= purchased) {
        toast.error(`You can select up to ${purchased} language(s). Purchase more on the pricing page.`);
        return prev;
      }
      return [...prev, code];
    });
  };

  const save = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/organizations/${orgId}/translation/languages`, {
        languages: selected,
      });
      toast.success('Languages saved');
      if (onUpdated) await onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save languages');
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/organizations/${orgId}/translation/languages`);
      setSelected(data.selected_languages || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Languages className="w-5 h-5 text-violet-500" />
          Translation & Localization
        </CardTitle>
        <CardDescription>
          English is included on every workspace. Choose up to {purchased} additional language
          {purchased !== 1 ? 's' : ''} for your documentation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Selected: {selected.length}/{purchased}
          {selected.length > 0
            ? ` · ${selected.map((code) => optionMap[code] || code).join(', ')}`
            : ' · none yet'}
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          {options.map((opt) => {
            const active = selected.includes(opt.code);
            const disabled = !active && selected.length >= purchased;
            return (
              <button
                key={opt.code}
                type="button"
                disabled={disabled}
                onClick={() => toggleLanguage(opt.code)}
                className={`text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="font-medium">{opt.name}</span>
                <span className="block text-xs text-slate-500 uppercase">{opt.code}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save languages
          </Button>
          <Button variant="outline" onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
